/**
 * Client-side Cyrex overlap detection (mirrors basket `with_invalid_reason` M2M rules).
 * GraphQL must include `overlappingProductIds` on basket line and addon products, and
 * `originalSampleOrderTestItem.cyrexAddonProducts { id name overlappingProductIds }`.
 */
export function cyrexProductsOverlap(productA, productB) {
  if (!productA?.id || !productB?.id) {
    return false;
  }
  const aId = String(productA.id);
  const bId = String(productB.id);
  const aOverlaps = productA.overlappingProductIds || [];
  const bOverlaps = productB.overlappingProductIds || [];
  const aToB = aOverlaps.map(String).includes(bId);
  const bToA = bOverlaps.map(String).includes(aId);
  return aToB || bToA;
}

function uniq(strings) {
  return [...new Set(strings)];
}

/**
 * @param {object} basketLineItem — parent `OrganisationBasketLineItem` from GraphQL
 * @param {object|null} addonLine — when invalid row is an add-on, the matching addon line object `{ id, product }`
 * @returns {string|null}
 */
export function buildOverlappingCyrexBasketDetailMessage(basketLineItem, addonLine) {
  if (!basketLineItem) {
    return null;
  }
  const hist = basketLineItem.originalSampleOrderTestItem?.cyrexAddonProducts ?? [];

  if (addonLine?.product) {
    const p = addonLine.product;
    const parts = [];
    let overlapsLinkedSample = false;
    for (const a of basketLineItem.addons || []) {
      if (String(a.id) === String(addonLine.id)) {
        continue;
      }
      const s = a.product;
      if (!s?.id || !cyrexProductsOverlap(p, s)) {
        continue;
      }
      parts.push(`“${s.name}” (another add-on in your basket)`);
    }
    for (const h of hist) {
      if (!h?.id || String(h.id) === String(p.id)) {
        continue;
      }
      if (!cyrexProductsOverlap(p, h)) {
        continue;
      }
      overlapsLinkedSample = true;
      parts.push(`“${h.name}” (already on the linked stored sample)`);
    }
    if (!parts.length) {
      return null;
    }
    const pairs = uniq(parts).join(" and ");
    const overlapExplain =
      "These Cyrex arrays are defined as overlapping — they cannot be used on the same sample.";
    const action = overlapsLinkedSample
      ? " You must remove the overlapping add-ons from your basket; the linked sample already includes those arrays."
      : " Remove one of the overlapping add-ons from your basket.";
    return `“${p.name}” overlaps ${pairs}. ${overlapExplain}${action}`;
  }

  const bits = [];
  let overlapsLinkedSample = false;
  const products = (basketLineItem.addons || []).map(a => a.product).filter(Boolean);
  for (let i = 0; i < products.length; i++) {
    for (let j = i + 1; j < products.length; j++) {
      const pi = products[i];
      const pj = products[j];
      if (cyrexProductsOverlap(pi, pj)) {
        bits.push(
          `“${pi.name}” and “${pj.name}” in your basket are overlapping (they cannot be combined)`
        );
      }
    }
  }
  for (const p of products) {
    for (const h of hist) {
      if (!h?.id || String(h.id) === String(p.id)) {
        continue;
      }
      if (!cyrexProductsOverlap(p, h)) {
        continue;
      }
      overlapsLinkedSample = true;
      bits.push(
        `“${p.name}” in your basket overlaps with “${h.name}” on the linked stored sample (same specimen — not allowed together)`
      );
    }
  }
  if (!bits.length) {
    return null;
  }
  const overlapExplain =
    "Overlapping Cyrex arrays are not permitted.";
  const action = overlapsLinkedSample
    ? " Please remove the overlapping add-ons from your basket."
    : " Remove overlapping add-ons from your basket until only one array from each overlap group remains.";
  return `${uniq(bits).join("; ")}. ${overlapExplain}${action}`;
}

/**
 * @param {object} basketLineItem — parent line with `originalSampleOrderTestItem`
 * @returns {string|null}
 */
export function buildInvalidCyrexSampleDetailMessage(basketLineItem) {
  const s = basketLineItem?.originalSampleOrderTestItem;
  if (!s) {
    return null;
  }
  const windowNote =
    s.isValidForCyrexSample === false
      ? "The linked sample is outside the valid window for new Cyrex add-on orders."
      : "";
  return `${windowNote}`;
}
